<template>
  <!-- Page Principale : Nouveau Profil Client -->
  <div class="px-6 py-6 space-y-6 min-h-screen select-none text-left transition-colors duration-200 font-sans"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">

    <!-- En-tête de page -->
    <div class="flex sm:flex-row flex-col justify-between items-start sm:items-center border-b pb-5 gap-4 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-0.5">
        <h1 class="text-xl font-semibold tracking-tight transition-colors duration-200" :class="isDark ? 'text-white' : 'text-zinc-900'">
          {{ isEditMode ? 'Éditer le Profil Client' : 'Créer un Nouveau Profil' }}
        </h1>
        <p class="text-xs font-medium transition-colors duration-200" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
          {{ isEditMode ? 'Modification de la fiche client et mise à jour des informations.' : 'Ouverture d\'une fiche client, configuration des préférences d\'assiduité et options de réservation.' }}
        </p>
      </div>

      <!-- Boutons d'actions supérieurs -->
      <div class="flex items-center gap-3">
        <button
          type="button"
          @click="resetForm"
          class="px-4 py-2 rounded-xl text-xs font-semibold border transition-colors cursor-pointer focus:outline-none shadow-sm"
          :class="isDark 
            ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-400 hover:text-white' 
            : 'bg-white hover:bg-zinc-100 border-zinc-200 text-zinc-600'"
        >
          Réinitialiser
        </button>
        <button
          type="button"
          @click="submitClient"
          :disabled="isSubmitting"
          class="px-4 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'"
        >
          {{ isSubmitting ? 'Enregistrement...' : (isEditMode ? 'Enregistrer les modifications' : 'Confirmer la création') }}
        </button>
      </div>
    </div>

    <!-- Contenu des sections du formulaire -->
    <div class="max-w-4xl space-y-4">

      <!-- Section 1 : Identité & Coordonnées -->
      <div class="border rounded-xl overflow-hidden transition-colors"
        :class="isDark ? 'border-zinc-900 bg-zinc-900/40' : 'border-zinc-200/80 bg-white shadow-sm'">
        <button
          type="button"
          class="w-full flex justify-between items-center px-5 py-3.5 font-semibold text-xs uppercase tracking-wider border-b focus:outline-none cursor-pointer transition-colors"
          :class="isDark 
            ? 'bg-zinc-900/80 hover:bg-zinc-900 text-zinc-300 hover:text-white border-zinc-900' 
            : 'bg-zinc-50 hover:bg-zinc-100/70 text-zinc-600 hover:text-zinc-900 border-zinc-200/60'"
          @click="toggleSection('identity')"
        >
          <span>01 / Identité & Coordonnées</span>
          <span class="text-[10px] font-normal transition-transform duration-300" :class="{ 'rotate-180': sections.identity, 'text-zinc-400': !sections.identity }">▼</span>
        </button>

        <div v-show="sections.identity" class="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 transition-colors"
          :class="isDark ? 'bg-zinc-950/30' : 'bg-zinc-50/20'">
          
          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Nom de famille *</label>
            <input type="text" v-model="form.nom" placeholder="Ex: Dupont" 
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all" 
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Prénom</label>
            <input type="text" v-model="form.prenom" placeholder="Ex: Jean" 
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all" 
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Adresse e-mail *</label>
            <input type="email" v-model="form.email" placeholder="exemple@mail.com" 
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all" 
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Téléphone mobile</label>
            <input type="tel" v-model="form.telephone" placeholder="06 12 34 56 78" 
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all" 
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Ville de résidence</label>
            <input type="text" v-model="form.ville" placeholder="Paris" 
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all" 
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Date de naissance</label>
            <input type="date" v-model="form.date_naissance" 
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all" 
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-zinc-300 focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-700 focus:border-zinc-300 shadow-sm'" />
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Civilité / Genre</label>
            <select v-model="form.genre" class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none appearance-none cursor-pointer transition-all"
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'">
              <option value="">Non spécifié</option>
              <option value="Homme">Homme</option>
              <option value="Femme">Femme</option>
              <option value="Autre">Autre</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Section 2 : Options Salon & Assiduité -->
      <div class="border rounded-xl overflow-hidden transition-colors"
        :class="isDark ? 'border-zinc-900 bg-zinc-900/40' : 'border-zinc-200/80 bg-white shadow-sm'">
        <button
          type="button"
          class="w-full flex justify-between items-center px-5 py-3.5 font-semibold text-xs uppercase tracking-wider border-b focus:outline-none cursor-pointer transition-colors"
          :class="isDark 
            ? 'bg-zinc-900/80 hover:bg-zinc-900 text-zinc-300 hover:text-white border-zinc-900' 
            : 'bg-zinc-50 hover:bg-zinc-100/70 text-zinc-600 hover:text-zinc-900 border-zinc-200/60'"
          @click="toggleSection('security')"
        >
          <span>02 / Préférences Salon & Options de Confiance</span>
          <span class="text-[10px] font-normal transition-transform duration-300" :class="{ 'rotate-180': sections.security, 'text-zinc-400': !sections.security }">▼</span>
        </button>

        <div v-show="sections.security" class="p-6 space-y-5 transition-colors" :class="isDark ? 'bg-zinc-950/30' : 'bg-zinc-50/20'">
          <div class="grid sm:grid-cols-2 gap-4">

            <!-- Option Blocage Fiche -->
            <div class="p-4 border rounded-xl flex items-center justify-between transition-colors"
              :class="isDark ? 'bg-zinc-950 border-zinc-900' : 'bg-white border-zinc-200 shadow-sm'">
              <div class="space-y-0.5 text-left pr-4">
                <p class="text-xs font-semibold transition-colors" :class="isDark ? 'text-white' : 'text-zinc-900'">Restreindre les réservations</p>
                <p class="text-[10px] font-medium transition-colors" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Empêcher la prise de rendez-vous en ligne pour ce profil.</p>
              </div>
              <input type="checkbox" v-model="form.blacklist" @change="handleStatusChange" class="h-4 w-4 rounded accent-rose-500 border-zinc-300 bg-zinc-100 cursor-pointer" />
            </div>

            <!-- Option Exigence d'Acompte -->
            <div class="p-4 border rounded-xl flex items-center justify-between transition-colors"
              :class="isDark ? 'bg-zinc-950 border-zinc-900' : 'bg-white border-zinc-200 shadow-sm'">
              <div class="space-y-0.5 text-left pr-4">
                <p class="text-xs font-semibold transition-colors" :class="isDark ? 'text-white' : 'text-zinc-900'">Exiger un acompte systématique</p>
                <p class="text-[10px] font-medium transition-colors" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Rendre le dépôt d'une empreinte de carte obligatoire pour valider.</p>
              </div>
              <input type="checkbox" v-model="form.acompte_demande" :disabled="form.blacklist" class="h-4 w-4 rounded accent-zinc-800 border-zinc-300 bg-zinc-100 cursor-pointer" />
            </div>

          </div>
        </div>
      </div>

      <!-- Section 3 : Notes Internes -->
      <div class="border rounded-xl overflow-hidden transition-colors"
        :class="isDark ? 'border-zinc-900 bg-zinc-900/40' : 'border-zinc-200/80 bg-white shadow-sm'">
        <button
          type="button"
          class="w-full flex justify-between items-center px-5 py-3.5 font-semibold text-xs uppercase tracking-wider border-b focus:outline-none cursor-pointer transition-colors"
          :class="isDark 
            ? 'bg-zinc-900/80 hover:bg-zinc-900 text-zinc-300 hover:text-white border-zinc-900' 
            : 'bg-zinc-50 hover:bg-zinc-100/70 text-zinc-600 hover:text-zinc-900 border-zinc-200/60'"
          @click="toggleSection('notes')"
        >
          <span>03 / Notes privées d'établissement</span>
          <span class="text-[10px] font-normal transition-transform duration-300" :class="{ 'rotate-180': sections.notes, 'text-zinc-400': !sections.notes }">▼</span>
        </button>

        <div v-show="sections.notes" class="p-6 space-y-1 transition-colors" :class="isDark ? 'bg-zinc-950/30' : 'bg-zinc-50/20'">
          <label class="text-[10px] uppercase tracking-wider font-semibold mb-1 block" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Commentaires de l'équipe (Masqués pour le client)</label>
          <textarea
            v-model="form.notes"
            rows="4"
            placeholder="Formules techniques favorites, habitudes d'accueil ou précisions sur les préférences de soins de votre client..."
            class="w-full p-4 rounded-xl border text-xs font-medium focus:outline-none resize-none transition-all"
            :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white placeholder-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 placeholder-zinc-400 shadow-sm'"
          ></textarea>
        </div>
      </div>

    </div>

    <!-- Encart de validation final inférieur -->
    <div class="max-w-4xl p-4 border rounded-2xl flex sm:flex-row flex-col items-start sm:items-center justify-between gap-4 transition-colors"
      :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200/80 shadow-sm'">
      <div class="space-y-0.5 text-left">
        <p class="text-xs font-semibold transition-colors" :class="isDark ? 'text-white' : 'text-zinc-900'">Initialisation de la nouvelle fiche</p>
        <p class="text-[11px] font-medium transition-colors" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
          Le compte s'ouvrira sous un statut actif avec un historique de rendez-vous vierge et une assiduité de référence à 100%.
        </p>
      </div>
      <button
        type="button"
        @click="submitClient"
        :disabled="isSubmitting"
        class="w-full sm:w-auto px-6 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer focus:outline-none shadow-sm"
        :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'"
      >
        {{ isSubmitting ? 'Enregistrement...' : 'Valider la fiche client' }}
      </button>
    </div>

  </div>
</template>
<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import api from '@/api'
import { useTheme } from '@/composables/useTheme';

// Variable globale injectée ou reçue par props pour basculer de thème
const { isDark } = useTheme();
const router = useRouter()
const route = useRoute()
const isSubmitting = ref(false)
const isEditMode = ref(false)
const clientId = ref(null)

// Section Visibility Control Engine
const sections = reactive({
  identity: true,
  security: false,
  notes: false
})

const toggleSection = (key) => {
  sections[key] = !sections[key]
}

// Sovereign Structure Object Schema for Client Registration
const form = reactive({
  nom: '',
  prenom: '',
  email: '',
  telephone: '',
  ville: '',
  date_naissance: '',
  genre: '',
  blacklist: false,
  acompte_demande: false,
  notes: ''
})

// Rule Engine: When a user forces 'Blacklisté' state, system automatically locks deposit rules
const handleStatusChange = () => {
  if (form.blacklist) {
    form.acompte_demande = true
  }
}

// Reset Local State Pipeline
const resetForm = () => {
  Object.assign(form, {
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    ville: '',
    date_naissance: '',
    genre: '',
    blacklist: false,
    acompte_demande: false,
    notes: ''
  })
  isEditMode.value = false
  clientId.value = null
}

// Load client data for edit mode
const loadClientData = async (id) => {
  try {
    const response = await api.clients.getClientById(id)
    const clientData = response.data

    // Pre-fill form with client data
    Object.assign(form, {
      nom: clientData.nom || '',
      prenom: clientData.prenom || '',
      email: clientData.email || '',
      telephone: clientData.telephone || '',
      ville: clientData.ville || '',
      date_naissance: clientData.date_naissance || '',
      genre: clientData.genre || '',
      blacklist: clientData.blacklist || clientData.profilRisque || false,
      acompte_demande: clientData.acompte_demande || false,
      notes: clientData.dernieresNotes || clientData.notes || ''
    })

    toast.success('Données du client chargées', {
      position: 'top-right',
      autoClose: 2000
    })
  } catch (error) {
    console.error('Erreur chargement client:', error)
    toast.error('Impossible de charger les données du client', {
      position: 'top-right',
      autoClose: 3000
    })
    router.push('/clients')
  }
}

// Check for edit mode on mount
onMounted(async () => {
  const editId = route.query.edit
  if (editId) {
    isEditMode.value = true
    clientId.value = editId
    await loadClientData(editId)
  }
})

// Emits payload validation sequence
const submitClient = async () => {
  if (isSubmitting.value) return

  // Validation
  if (!form.nom || !form.email) {
    toast.error('Le nom et l\'email sont obligatoires', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  try {
    isSubmitting.value = true

    const clientData = {
      nom: form.nom,
      prenom: form.prenom,
      email: form.email,
      telephone: form.telephone,
      ville: form.ville,
      date_naissance: form.date_naissance,
      genre: form.genre,
      blacklist: form.blacklist,
      acompte_demande: form.acompte_demande,
      notes: form.notes
    }

    if (isEditMode.value && clientId.value) {
      // Update existing client
      await api.clients.updateClient(clientId.value, clientData)

      toast.success(`Le profil de "${form.nom}" a été mis à jour !`, {
        position: 'top-right',
        autoClose: 2000
      })
    } else {
      // Create new client
      await api.clients.createClient(clientData)

      toast.success(`Le profil de "${form.nom}" est officiellement enrôlé !`, {
        position: 'top-right',
        autoClose: 2000
      })
    }

    // Redirection vers la liste des clients après succès
    setTimeout(() => {
      router.push('/clients')
    }, 500)

  } catch (error) {
    console.error(`Erreur lors de ${isEditMode.value ? 'la mise à jour' : 'la création'}:`, error)
    toast.error(
      error.response?.data?.message || `Erreur lors de ${isEditMode.value ? 'la mise à jour' : 'la création'} du client`,
      {
        position: 'top-right',
        autoClose: 3000
      }
    )
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.25s ease-out forwards;
}
</style>
